import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { tasksApi, taskTypesApi, launchesApi } from "../../api/endpoints";
import { isOverdue } from "./TaskCard";
import { useSession } from "../../hooks/useSession";
import { parseIsoLocal } from "../../lib/weekDates";
import type { Task, TaskRiskLevel, TaskStatus, TaskType, Launch } from "../../types/domain";

interface Props {
  onOpen: (task: Task) => void;
  /** Опциональный пред-фильтр по запуску. */
  launchFilter?: (t: Task) => boolean;
}

type Preset = "all" | "mine" | "no-assignee" | "high-risk";

const PRESETS: Array<{ id: Preset; label: string; hint: string }> = [
  { id: "all", label: "Все", hint: "Все задачи воркспейса" },
  { id: "mine", label: "Мои", hint: "Задачи, где я ответственный" },
  { id: "no-assignee", label: "Без ответственного", hint: "Никто не назначен" },
  { id: "high-risk", label: "Высокий риск", hint: "Критично или просрочено" },
];

const STATUS_LABEL: Record<TaskStatus, string> = {
  planned: "Запланировано",
  in_progress: "В работе",
  done: "Готово",
  cancelled: "Отменено",
};

const RISK_LABEL: Record<TaskRiskLevel, string> = {
  none: "—",
  risk: "Риск",
  critical: "Критично",
};

const MONTH_GENITIVE = [
  "янв", "фев", "мар", "апр", "мая", "июн",
  "июл", "авг", "сен", "окт", "ноя", "дек",
];

function formatDate(iso: string | null, withTime?: string | null): string {
  if (!iso) return "—";
  const d = parseIsoLocal(iso);
  const head = `${d.getDate()} ${MONTH_GENITIVE[d.getMonth()] ?? ""}`;
  return withTime ? `${head}, ${withTime}` : head;
}

export function TasksAllView({ onOpen, launchFilter }: Props) {
  const me = useSession().data ?? null;
  const [preset, setPreset] = useState<Preset>("all");

  const tasksQuery = useQuery({
    queryKey: ["tasks", "all"],
    queryFn: async () => (await tasksApi.list({ includeUndated: true })).tasks,
    staleTime: 5_000,
  });

  const taskTypesQuery = useQuery({
    queryKey: ["taskTypes"],
    queryFn: async () => (await taskTypesApi.list()).types,
    staleTime: 60_000,
  });

  const launchesQuery = useQuery({
    queryKey: ["launches"],
    queryFn: async () => (await launchesApi.list()).launches,
    staleTime: 30_000,
  });

  const typeById = useMemo(
    () => new Map<string, TaskType>((taskTypesQuery.data ?? []).map((t) => [t.id, t])),
    [taskTypesQuery.data],
  );
  const launchById = useMemo(
    () => new Map<string, Launch>((launchesQuery.data ?? []).map((l) => [l.id, l])),
    [launchesQuery.data],
  );

  const filtered = useMemo(() => {
    const all = (tasksQuery.data ?? []).filter((t) =>
      launchFilter ? launchFilter(t) : true,
    );
    const meId = me?.id ?? null;
    const list = all.filter((t) => {
      if (preset === "mine") {
        if (!meId) return false;
        return t.assigneeId === meId;
      }
      if (preset === "no-assignee") return !t.assigneeName;
      if (preset === "high-risk") {
        if (t.status === "done" || t.status === "cancelled") return false;
        return t.riskLevel === "critical" || isOverdue(t);
      }
      return true;
    });
    return list.sort(byDueThenPlanned);
  }, [tasksQuery.data, preset, me?.id, launchFilter]);

  if (tasksQuery.isLoading) {
    return <div className="tasks-stub">Загружаем…</div>;
  }
  if (tasksQuery.isError) {
    return (
      <div className="tasks-stub" style={{ color: "#DC2626" }}>
        Не удалось загрузить задачи.
      </div>
    );
  }

  return (
    <div className="tasks-all">
      <div className="tasks-all-presets" role="tablist">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            type="button"
            role="tab"
            aria-selected={preset === p.id}
            className={"tasks-all-preset" + (preset === p.id ? " is-active" : "")}
            onClick={() => setPreset(p.id)}
            title={p.hint}
          >
            {p.label}
          </button>
        ))}
        <span className="tasks-all-count">
          <b>{filtered.length}</b> {pluralTasks(filtered.length)}
        </span>
      </div>

      {filtered.length === 0 ? (
        <div className="tasks-stub">Под этот фильтр ничего не подходит.</div>
      ) : (
        <div className="tasks-all-table-wrap">
          <table className="tasks-all-table">
            <thead>
              <tr>
                <th>Задача</th>
                <th className="col-launch">Запуск</th>
                <th className="col-due">Дедлайн</th>
                <th className="col-planned">Запланировано на</th>
                <th className="col-status">Статус</th>
                <th className="col-risk">Риск</th>
                <th className="col-assignee">Ответственный</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((t) => {
                const launch = t.launchId ? launchById.get(t.launchId) ?? null : null;
                const overdue = isOverdue(t);
                const type = typeById.get(t.taskTypeId);
                return (
                  <tr
                    key={t.id}
                    className={
                      "tasks-all-row" +
                      (overdue ? " is-overdue" : "") +
                      (t.riskLevel === "critical" ? " is-critical" : "")
                    }
                    onClick={() => onOpen(t)}
                  >
                    <td className="cell-title">
                      {type && (
                        <span
                          className="tasks-all-type-dot"
                          style={{ background: type.color }}
                          title={type.name}
                        />
                      )}
                      <span className="cell-title-text">{t.title}</span>
                    </td>
                    <td className="col-launch">
                      {launch ? (
                        <span
                          className="tasks-all-launch"
                          title={launch.name}
                        >
                          <span
                            className="tasks-all-launch-dot"
                            style={{ background: launch.color ?? "var(--accent)" }}
                          />
                          {launch.name}
                        </span>
                      ) : (
                        <span className="tasks-all-mute">—</span>
                      )}
                    </td>
                    <td className="col-due">
                      <span className={overdue ? "tasks-all-overdue" : ""}>
                        {formatDate(t.dueDate, t.dueTime)}
                      </span>
                    </td>
                    <td className="col-planned">{formatDate(t.plannedDate)}</td>
                    <td className="col-status">
                      <span className={"tasks-all-status status-" + t.status}>
                        {STATUS_LABEL[t.status]}
                      </span>
                    </td>
                    <td className="col-risk">
                      <span className={"tasks-all-risk risk-" + t.riskLevel}>
                        {RISK_LABEL[t.riskLevel]}
                      </span>
                    </td>
                    <td className="col-assignee">
                      {t.assigneeName || <span className="tasks-all-mute">не назначен</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** Сортировка: задачи с дедлайном идут раньше (по возрастанию dueDate),
 *  потом по plannedDate, потом по createdAt. Внутри одной даты — открытые задачи раньше готовых. */
function byDueThenPlanned(a: Task, b: Task): number {
  const aClosed = a.status === "done" || a.status === "cancelled" ? 1 : 0;
  const bClosed = b.status === "done" || b.status === "cancelled" ? 1 : 0;
  if (aClosed !== bClosed) return aClosed - bClosed;

  if (a.dueDate && b.dueDate) {
    if (a.dueDate !== b.dueDate) return a.dueDate.localeCompare(b.dueDate);
  } else if (a.dueDate) return -1;
  else if (b.dueDate) return 1;

  if (a.plannedDate && b.plannedDate) {
    if (a.plannedDate !== b.plannedDate) return a.plannedDate.localeCompare(b.plannedDate);
  } else if (a.plannedDate) return -1;
  else if (b.plannedDate) return 1;

  return a.createdAt.localeCompare(b.createdAt);
}

function pluralTasks(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return "задача";
  if ([2, 3, 4].includes(mod10) && ![12, 13, 14].includes(mod100)) return "задачи";
  return "задач";
}
